import React from 'react';
import Node, {
  NodeProps,
} from 'components/canvas/components/nodes/components/base-node';
import { defaultNodeDimensions } from 'components/canvas/components/nodes';
import NodeHeader from 'components/canvas/components/nodes/components/base-node/components/node-header';
import ProtocolNodeSubscriptionListeners from 'components/canvas/components/nodes/components/protocol-node/components/protocol-node-subscription-listeners';
import NodeContextMenu from 'components/canvas/components/nodes/components/base-node/components/node-context-menu';
import GlobalLinksIndicator from 'components/canvas/components/nodes/components/base-node/components/global-links-indicator';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useNodeDimensionsOperations from 'components/canvas/hooks/use-node-dimensions-operations';
import useUpdateNodePosition from 'components/canvas/hooks/use-update-node-position';
import useProtocolEventListeners, {
  useOnNodeAddedToProtocol,
  useOnNodeEjectedFromProtocol,
} from 'components/canvas/components/nodes/components/protocol-node/hooks/use-protocol-event-listeners';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useNodeComponent from 'components/canvas/hooks/use-node-component';
import useProtocolOperations from 'components/canvas/components/nodes/components/protocol-node/hooks/use-protocol-operations';
import useFetchNodeFromCacheById from 'components/canvas/hooks/use-fetch-node-from-cache-by-id';
import { useSurfaceNodeModalsOperations } from 'routes/surface/hooks/use-surface-node-modals-context';
import { isUndefined } from 'lodash';
import { compose, set } from 'lodash/fp';
import { Position } from 'types';
import { SurfaceProtocolNode, SurfaceNodeUnion } from 'graphql/types';
import Konva from 'konva';

interface ProtocolNodeProps extends NodeProps {
  node: SurfaceProtocolNode;
}

export const PROTOCOL_PADDING = 70;

function ProtocolNode(props: ProtocolNodeProps) {
  const { node } = props;

  const nodeDimensions = defaultNodeDimensions;

  const draggedShapeInstanceRef = React.useRef<Konva.Node | null>(null);

  const {
    findProtocolContainingNodeShape,
    shapesDoNotHaveIntersection,
  } = useProtocolOperations();

  let containingNode = node.contains;

  let { width, height } = nodeDimensions.protocol ?? nodeDimensions.general;

  const saveNodeInCache = useSaveNodeInCache();

  const position = node.position as Position;

  useProtocolEventListeners(node);

  const { updateNodePositionInCache } = useUpdateNodePosition();

  const { getNodeDimensionsByType } = useNodeDimensionsOperations();

  const { showNodeModal } = useSurfaceNodeModalsOperations();

  const ContainingNode = useNodeComponent();

  const fetchNodeFromCacheById = useFetchNodeFromCacheById();

  if (containingNode?.type) {
    const containingNodeDimensions = getNodeDimensionsByType(
      containingNode?.type,
    );

    width = containingNodeDimensions.width + PROTOCOL_PADDING * 2;
    height = containingNodeDimensions.height + PROTOCOL_PADDING * 2;
  }

  function onAfterDelete() {
    if (containingNode && node.isReferenceCopy === false) {
      saveNodeInCache({
        ...containingNode,
        position,
      });

      updateNodePositionInCache(containingNode, position);
    }
  }

  function onDragStart() {
    // if(node.uploader){
    //   return
    // }
    if (containingNode) {
      draggedShapeInstanceRef.current = findProtocolContainingNodeShape(node);
    }
  }

  function onDragEnd() {
    draggedShapeInstanceRef.current = null;
  }

  function onDragMove(event) {
    if (containingNode) {
      const position = event.target.position();

      const shapeInstance = draggedShapeInstanceRef.current;

      if (shapeInstance) {
        shapeInstance.x(position.x + PROTOCOL_PADDING);
        shapeInstance.y(position.y + PROTOCOL_PADDING);
      }
    }
  }

  const [clonedContainingNode, setClonedContainingNode] = React.useState(null);

  const { findShapeById } = useCanvas();

  React.useEffect(() => {
    if (containingNode) {
      const containingNodeShape = findShapeById(containingNode?.id);

      const protocolShape = findShapeById(node.id);

      // If node is on another surface add it

      const onDifferentSurface =
        node?.surface?.id !== containingNode?.surface?.id;

      const notDraggedIntoThisProtocol =
        containingNodeShape &&
        protocolShape &&
        shapesDoNotHaveIntersection(containingNodeShape, protocolShape);

      if (onDifferentSurface || notDraggedIntoThisProtocol) {
        setClonedContainingNode(
          compose(
            set('position.x', node?.position?.x + PROTOCOL_PADDING),
            set('position.y', node?.position?.y + PROTOCOL_PADDING),
          )(containingNode),
        );
      }
    }
  }, []);

  useOnNodeAddedToProtocol(node, (containingNode: SurfaceNodeUnion) => {
    const containingNodeShape = findProtocolContainingNodeShape(node);

    const protocolShape = findShapeById(node.id);

    // If node is on another surface add it

    const onDifferentSurface =
      node?.surface?.id !== containingNode?.surface?.id;

    const notDraggedIntoThisProtocol =
      isUndefined(containingNodeShape) ||
      (containingNodeShape &&
        protocolShape &&
        shapesDoNotHaveIntersection(containingNodeShape, protocolShape));

    if (onDifferentSurface || notDraggedIntoThisProtocol) {
      setClonedContainingNode(
        compose(
          set('position.x', node?.position?.x + PROTOCOL_PADDING),
          set('position.y', node?.position?.y + PROTOCOL_PADDING),
        )(containingNode),
      );
    }
  });

  useOnNodeEjectedFromProtocol(node, (previousContainingNode) => {
    const onDifferentSurface =
      node?.surface?.id !== previousContainingNode?.surface?.id;

    if (onDifferentSurface) {
      // Remove node from cache
    }

    clonedContainingNode && setClonedContainingNode(null);
  });

  return (
    <>
      <ProtocolNodeSubscriptionListeners
        id={node.id}
        isReference={node.isReference}
        containingNodeId={containingNode?.id}
      />

      <Node
        {...props}
        width={width}
        height={height}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragMove={onDragMove}
        style={{
          fill: '#E5E5E5',
          stroke: '#A5A5A5',
          strokeWidth: 3,
          dash: [5, 5],
        }}>
        {({ Rect }) => {
          return (
            <>
              <GlobalLinksIndicator node={node} x={width} />
              {Rect}
              <NodeHeader
                x={0}
                offsetY={45}
                fill="#1C1B18"
                fontStyle="bold"
                text={node.name ?? ''}
                width={width - 30}
                isReference={node.isReference}
              />

              <NodeContextMenu
                node={node}
                width={width}
                height={height}
                onAfterDelete={onAfterDelete}
              />
            </>
          );
        }}
      </Node>

      {Boolean(clonedContainingNode) && (
        <ContainingNode
          node={clonedContainingNode}
          showNodeModal={showNodeModal}
          groupProps={{ inProtocol: node.id }}
        />
      )}
    </>
  );
}

export default ProtocolNode;
